/**
 * Extract the main selling price from text.
 *
 * Remarks often carry several prices: the main product price plus lower prices
 * for optional accessories ("Optional: USB extender $8"). A price is treated as
 * an accessory price when an optional/add-on/accessory marker precedes it in its
 * own segment (or tags it immediately after without naming a price of its own,
 * "$8 (optional)"); those only win as a last resort. A price preceded by a
 * total/tax-inclusive marker ("Total:", "with GST") is a line total, not a unit
 * price, so it ranks below both. Among main-price candidates the highest wins,
 * because the main product price is normally the largest amount quoted.
 *
 * @param text - Text to extract price from
 * @returns Extracted price as number or null if no price found
 */
export const extractPriceFromText = (text: string): number | null => {
  if (!text) return null;

  // Require a $ immediately before the number, with optional S/US/SGD/USD prefix
  // (the prefix must be a standalone token, not the tail of a preceding word)
  const priceRegex = /(?:(^|[^A-Za-z])(?:SGD|USD|S|US)\s*)?\$\s*(\d+(?:,\d{3})*(?:\.\d{1,2})?)/gi;

  // Words that mark the following prices in their segment as optional accessories
  const optionalMarker = /\b(?:optional|opt|add[-\s]?ons?|accessor(?:y|ies))\b/i;
  // "…$8 (optional)" — a bracketed tag right after the price marks it optional,
  // but only when the tag introduces no further price before it ends: in
  // "$199 (optional stand $30)" the tag belongs to the accessory inside it.
  // The tag ends at its closing bracket or, since the after-context is already
  // cut at the segment boundary, at the end of that context.
  const optionalAfter = /^\s*[([]\s*(?:optional|add[-\s]?ons?)\b[^)\]$]*(?:[)\]]|$)/i;
  // Words that mark the following prices in their segment as line totals or
  // tax-inclusive amounts; quoted_price is per unit, so these are never main
  const totalMarker =
    /\b(?:sub[-\s]?)?total\b|\bamount\b|\bw\/\s*gst\b|\bwith\s+gst\b|\bincl(?:uding|usive)?(?:\s+of)?\s+gst\b|\bafter\s+gst\b/i;

  // Segment separators; a marker applies until the next one. A period is only a
  // boundary outside decimals like "USB 3.0". Both the split used on the
  // before-price gap and the scan used on the after-price context derive from
  // this one pattern so they cannot disagree about where a segment ends.
  const boundarySource = '[\\n\\r;|\\u2022]|\\.(?!\\d)';
  const boundarySplit = new RegExp(boundarySource);
  const boundaryAt = new RegExp(boundarySource, 'y');
  const isBoundaryAt = (s: string, i: number) => {
    boundaryAt.lastIndex = i;
    return boundaryAt.test(s);
  };

  const mainPrices: number[] = [];
  const optionalPrices: number[] = [];
  const totalPrices: number[] = [];
  let optionalActive = false;
  let totalActive = false;
  let cursor = 0;

  let match: RegExpExecArray | null;
  while ((match = priceRegex.exec(text)) !== null) {
    const matchStart = match.index + (match[1] ? match[1].length : 0);
    const matchEnd = match.index + match[0].length;

    // Update the marker state from the text since the previous price, treating
    // an abbreviation's dot ("opt.", "incl.") as part of the word, not a boundary
    const gap = text.slice(cursor, matchStart).replace(/\b(opt|incl)\./gi, '$1 ');
    const segments = gap.split(boundarySplit);
    if (segments.length > 1) {
      optionalActive = false;
      totalActive = false;
    }
    const segment = segments[segments.length - 1];
    if (optionalMarker.test(segment)) optionalActive = true;
    if (totalMarker.test(segment)) totalActive = true;
    cursor = matchEnd;

    const price = parseFloat(match[2].replace(/,/g, ''));
    if (isNaN(price) || price <= 0 || price > 999999) continue;

    // Context after the price: only up to the next segment boundary
    let segEnd = matchEnd;
    while (segEnd < text.length && !isBoundaryAt(text, segEnd)) segEnd++;
    const after = text.slice(matchEnd, segEnd);

    if (totalActive) totalPrices.push(price);
    else if (optionalActive || optionalAfter.test(after)) optionalPrices.push(price);
    else mainPrices.push(price);
  }

  // Prefer prices in typical ranges (0.01 to 10,000), then take the highest
  const pick = (prices: number[]): number | null => {
    if (prices.length === 0) return null;
    const reasonable = prices.filter(p => p >= 0.01 && p <= 10000);
    return Math.max(...(reasonable.length > 0 ? reasonable : prices));
  };

  return pick(mainPrices) ?? pick(optionalPrices) ?? pick(totalPrices);
};

/**
 * Remove the extracted price from a string, written plain or with thousands
 * separators. Any other price in the text (an accessory price, a total) is left
 * in place so the salesperson still sees it in the saved remark.
 * @param text - Original text
 * @param extractedPrice - The price that was extracted (to help identify what to remove)
 * @returns Text with price information removed
 */
export const removePriceFromText = (text: string, extractedPrice: number): string => {
  if (!text || !extractedPrice) return text;

  const priceStr = extractedPrice.toString();
  const formattedPrice = extractedPrice.toFixed(2);

  // The price may be written with thousands separators ("S$1,299.00"), which the
  // plain toString/toFixed rendering never matches, so accept both renderings
  const withCommas = (value: string): string => {
    const [whole, fraction] = value.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fraction === undefined ? grouped : `${grouped}.${fraction}`;
  };
  const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const alternation = (value: string): string =>
    `(?:${Array.from(new Set([value, withCommas(value)])).map(escapeRegExp).join('|')})`;

  // The full two-decimal rendering is tried first, and a match may not stop
  // part-way through a number: 12.5 must take all of "S$12.50" (not leave a
  // stray "0"), and a price of 12 must not eat the "$12" of "$120".
  const notMidNumber = '(?!\\d|[.,]\\d)';
  const numberPattern =
    `(?:${alternation(formattedPrice)}|${alternation(priceStr)}(?:\\.\\d{1,2})?)${notMidNumber}`;
  const formattedPattern = `${alternation(formattedPrice)}${notMidNumber}`;

  // Patterns to remove (only those with an explicit $ sign)
  const removalPatterns = [
    // S$ 123.45 or S$123.45
    new RegExp(`S\\$\\s*${numberPattern}`, 'gi'),
    // US$ 123.45 or US$123.45
    new RegExp(`US\\$\\s*${numberPattern}`, 'gi'),
    // SGD $ 123.45 (explicit $ required)
    new RegExp(`SGD\\s*\\$\\s*${numberPattern}`, 'gi'),
    // USD $ 123.45 (explicit $ required)
    new RegExp(`USD\\s*\\$\\s*${numberPattern}`, 'gi'),
    // Generic $ 123.45
    new RegExp(`\\$\\s*${numberPattern}`, 'gi'),
    // Also match when the text contains the exact formatted price with an immediate $
    new RegExp(`\\$\\s*${formattedPattern}`, 'gi'),
  ];

  let cleanedText = text;
  
  // Apply each removal pattern
  for (const pattern of removalPatterns) {
    cleanedText = cleanedText.replace(pattern, '');
  }
  
  // Clean up extra spaces and punctuation
  cleanedText = cleanedText
    .replace(/\s+/g, ' ')
    .replace(/\s*[,;]\s*/g, ', ')
    .replace(/^\s*[,;]\s*/, '')
    .replace(/\s*[,;]\s*$/, '')
    .trim();

  return cleanedText;
};
